import React, { useState } from 'react';
import { useMutation, queryCache } from 'react-query';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Select from '../components/Select';
import api from '../api';

const inputStyle = {
  width: '100%',
  padding: 10,
  marginTop: 4,
  marginBottom: 12,
  border: '1px solid #999',
  borderRadius: 0,
  fontSize: 15,
  boxSizing: 'border-box',
};

const labelStyle = { display: 'block', fontWeight: 'bold', fontSize: 13 };

// Gutters are done with negative margin + padding rather than flex `gap`, which
// older iOS Safari ignores — there it would collapse the spacing to zero.
const rowStyle = { display: 'flex', flexWrap: 'wrap', margin: '0 -8px' };
const colStyle = { flex: '1 1 200px', padding: '0 8px', boxSizing: 'border-box' };
const buttonRowStyle = { display: 'flex', flexWrap: 'wrap', margin: '0 -5px' };
const buttonCellStyle = { padding: '0 5px', marginBottom: 8 };

/**
 * Forward (Weiterleiten) a parking card to a recipient — the web mirror of the
 * app's parking share modal. Both actions post recipient_* to
 * PUT parking/access/:id/share, which issues the public link.
 *
 * "Senden" also delivers that link, by SMS or email depending on what was
 * filled in. "Link kopieren" delivers nothing and just hands back the link, but
 * still records the recipient's name — otherwise a card could be passed on with
 * no trace of who received it. Either way the recipient is stored, so the form
 * reopens prefilled with whoever holds the card.
 * @component
 */
const ParkingShareModal = ({ card, contacts = [], onClose }) => {
  const [token, setToken] = useState(card.anonymous_token || '');
  const link = token ? `${window.location.origin}/parking-eticket/${token}` : '';

  // Prefilled from the stored recipient, so reopening the dialog shows who the
  // card currently sits with rather than a blank form.
  const [firstName, setFirstName] = useState(card.recipient_first_name || '');
  const [lastName, setLastName] = useState(card.recipient_last_name || '');
  const [phone, setPhone] = useState(card.recipient_phone || '');
  const [email, setEmail] = useState(card.recipient_email || '');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const refresh = () => queryCache.invalidateQueries('parking-me');

  // Same contact picker as the Saisonabo dialog, so a card can be forwarded to
  // a saved contact without retyping their details.
  const contactList = contacts || [];
  const contactOptions = contactList.map((contact) => ({
    name: [contact.last_name, contact.first_name].filter(Boolean).join(' ') || contact.phone || '',
    value: contact.id,
  }));

  const selectContact = (event) => {
    const picked = contactList.find((contact) => contact.id === parseInt(event.target.value, 10));

    if (!picked) return;

    setFirstName(picked.first_name || '');
    setLastName(picked.last_name || '');
    setPhone(picked.phone || '');
    setEmail(picked.email || '');
    setError('');
  };

  // Omitting phone and email is what keeps the copy-link flow silent: the
  // backend only sends on the channels it is given.
  const recipient = (withChannels) => ({
    recipient_first_name: firstName.trim(),
    recipient_last_name: lastName.trim(),
    recipient_phone: withChannels && phone.trim() ? phone.trim() : undefined,
    recipient_email: withChannels && email.trim() ? email.trim() : undefined,
  });

  const onShareError = () => setError('Etwas ist schief gelaufen');

  const [doSend, { isLoading: sending }] = useMutation(
    () => api.parking.share(card.access_code_id, recipient(true)),
    {
      onSuccess() {
        refresh();
        onClose();
      },
      onError: onShareError,
    },
  );

  const [doCopy, { isLoading: linking }] = useMutation(
    () => api.parking.share(card.access_code_id, recipient(false)),
    {
      onSuccess(res) {
        const issued = res?.data?.anonymous_token;
        if (issued) setToken(issued);
        refresh();

        // Copying right after the request can be refused by the browser, so the
        // link is shown as well and stays copyable by hand.
        const url = `${window.location.origin}/parking-eticket/${issued}`;
        if (issued && navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => setCopied(true)).catch(() => {});
        }
      },
      onError: onShareError,
    },
  );

  const busy = sending || linking;

  // The name is what makes the share traceable, so it is required either way.
  const missingName = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Vorname und Nachname sind erforderlich');
      return true;
    }
    setError('');
    return false;
  };

  const submitSend = () => {
    if (missingName()) return;

    if (!phone.trim() && !email.trim()) {
      setError('Telefon oder Email ist erforderlich');
      return;
    }

    doSend();
  };

  const submitCopy = () => {
    if (missingName()) return;

    setCopied(false);
    doCopy();
  };

  return (
    <Modal isOpen title="Parkkarte weiterleiten" size="medium" onRequestClose={onClose}>
      <div style={{ color: '#000' }}>
        {!!contactOptions.length && (
          <Select
            label="Wählen Sie aus Ihren Kontakten aus"
            options={contactOptions}
            labelInOption="Kontakte"
            theme="light"
            onOptionChange={selectContact}
          />
        )}

        {!!link && (
          <div style={{ marginBottom: 16 }}>
            <span style={labelStyle}>Aktiver Link</span>
            <div style={{ wordBreak: 'break-all', fontSize: 13, margin: '4px 0 0' }}>{link}</div>
          </div>
        )}

        <div style={rowStyle}>
          <div style={colStyle}>
            <span style={labelStyle}>Vorname *</span>
            <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>

          <div style={colStyle}>
            <span style={labelStyle}>Nachname *</span>
            <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>

        <div style={rowStyle}>
          <div style={colStyle}>
            <span style={labelStyle}>Telefon</span>
            <input
              style={inputStyle}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+41791234567"
            />
          </div>

          <div style={colStyle}>
            <span style={labelStyle}>Email</span>
            <input style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>

        {!!error && <p style={{ color: '#c8102e', fontSize: 13, marginTop: 0 }}>{error}</p>}

        <div style={buttonRowStyle}>
          <div style={buttonCellStyle}>
            <Button loading={sending} disabled={busy} onClick={submitSend}>
              Senden
            </Button>
          </div>

          <div style={buttonCellStyle}>
            <Button loading={linking} disabled={busy} onClick={submitCopy}>
              {copied ? 'Kopiert' : 'Link kopieren'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ParkingShareModal;
